import { ref, computed } from "vue";
import { defineStore } from "pinia";
import {
  getSchedule,
  getProductionCalendar,
  getPositions,
  getDayTypes,
  getShiftSchedule,
  getShiftHistory,
  removeShift,
  getTimeSheet,
  createUpdateShiftPack,
  getTimesheetReport,
  getConsolidatedReport,
  getScheduleReport,
  getDisciplineReport,
  getFraudReport,
  validateShift,
  cleanSchedule,
  generateSchedule,
  getStats,
} from "../api/schedule";
import { getEmploymentSchedule } from "@/api/users";
import type { ShiftCopyRequest } from "@/api/schedule";
import {
  Schedule,
  Position,
  DayType,
  Shift,
  Department,
  ScheduleRules,
} from "@/types";
import dayjs, { type Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import isoWeek from "dayjs/plugin/isoWeek";
import ru from "dayjs/locale/ru";
import { useToast } from "vue-toast-notification";
import { useDepartmentStore } from "./departmentStore";
import { useAuthStore } from "./authStore";
import {
  type ScheduleView,
  DATE_FORMAT,
  readStoredScheduleView,
  writeStoredScheduleView,
  weekDates,
  weekStartForMonth,
  weekStartOf,
  collectTimesheetShiftHours,
  type TimesheetShiftHours,
} from "@/components/schedule/weekSchedule";

dayjs.locale(ru);
dayjs.extend(utc);
dayjs.extend(isoWeek);

const toast = useToast({ position: "bottom-right" });

// Узел дерева подразделений с потомками, выбранный в Аналитике целиком.
export interface AnalyticsGroup {
  id: string;
  name: string;
}

interface RuleCheckContext {
  department: string | undefined;
  month: string;
  year: string;
}

export const useScheduleStore = defineStore("schedule", () => {
  // Состояния
  const schedules = ref<Schedule[]>([]);
  const rules = ref<ScheduleRules | null>(null);
  const timesheets = ref<any[]>([]);
  const stats = ref();
  const schedulesSortedByName = ref<Schedule[]>([]);
  const schedulesSortedByPosition = ref<Schedule[]>([]);
  const prodCalendar = ref<any[]>([]);
  const positions = ref<Position[]>([]);
  const dayTypes = ref<DayType[]>([]);
  const shiftSchedules = ref<any[]>([]);
  const selectedEmploymentSchedule = ref<any>(null);
  const shiftHistory = ref<any[]>([]);
  const currentShift = ref<Shift | null>(null);
  const currentShifts = ref<Shift[]>([]);
  const originalShifts = ref<Shift[]>([]); // оригинальные сохранённые смены для отслеживания изменений
  const changedShifts = ref<string[]>([]); // массив ID изменённых смен
  const isCurrentShiftChanged = ref(false)
  // В недельном виде currentScheduleDate — воскресенье недели: месячный контекст = месяц окончания недели.
  const currentScheduleDate = ref(dayjs());
  const scheduleView = ref<ScheduleView>("month");
  const currentWeekStart = ref<Dayjs>(weekStartOf(dayjs()));
  const isTimesheetMode = ref(false);
  const currentDepartment = ref<Department>();
  const analyticsGroup = ref<AnalyticsGroup | null>(null);
  const refresh = ref(0);
  const users = ref<any[]>([]);
  const loading = ref<boolean>(false);
  let scheduleRequest = 0;
  let backgroundScheduleRequest = 0;
  let rulesRequest = 0;
  let statsRequest = 0;
  let prodCalendarRequest = 0;
  let weekTimesheetRequest = 0;
  let timesheetRequest = 0;
  // Готовность месячного табеля отдельно от графика: таблица показывает данные только своего подразделения/месяца.
  const isTimesheetLoading = ref(false);
  const loadedTimesheetKey = ref<string | null>(null);
  // Рассчитанные часы смен недели из месячных табелей, по shift ID. Отдельно от timesheets месячного табеля.
  const weekShiftHours = ref<Map<string, TimesheetShiftHours>>(new Map());
  // Месячные табели всех месяцев недели (ключ YYYY-MM): недельный табель (RD-139) берёт дни по полной дате.
  // Готова только неделя, у которой загружены все месяцы, — иначе незагруженный месяц не рисуется как «ВЫХ».
  const weekTimesheets = ref<Record<string, any[]>>({});
  const isWeekTimesheetLoading = ref(false);
  const loadedWeekTimesheetKey = ref<string | null>(null);
  // После изменения смен загруженный табель устарел до ответа повторной загрузки: табель (не график —
  // у него своя проверка актуальности часов по времени смены) не показывает прежние агрегаты как актуальные.
  const isTimesheetRefreshPending = ref(false);
  const isWeekTimesheetRefreshPending = ref(false);
  // Показатели сотрудника (в т. ч. норма часов) — только своего подразделения/месяца: прежние не выдаются за текущие.
  const loadedStatsKey = ref<string | null>(null);
  const isSaveShiftEnabled = ref(false);
  const shiftFieldsClicked = ref(false);
  const isShiftValid = ref<boolean>(false);
  const userStat = ref<any>(null);
  const userStat2 = ref<any>(null);
  const isLightTheme = ref(false);
  const isCellHighlighting = ref(false);
  const isDayStatsVisible = ref(false);
  const isEmployeeStatsVisible = ref(false);

  const departmentStore = useDepartmentStore();
  const authStore = useAuthStore();

  // Вычисляемые свойства
  const currentMonthDays = computed(() =>
    currentScheduleDate.value.daysInMonth(),
  );
  const currentMonthStart = computed(() =>
    currentScheduleDate.value.date(1).format("YYYY-MM-DD"),
  );
  const currentMonthEnd = computed(() =>
    currentScheduleDate.value.date(currentMonthDays.value).format("YYYY-MM-DD"),
  );
  // Период — неделя в графике и в табеле (RD-139); isWeekView — именно недельный график.
  const isWeekPeriod = computed(() => scheduleView.value === "week");
  const isWeekView = computed(() => isWeekPeriod.value && !isTimesheetMode.value);
  const isTimesheetWeekView = computed(() => isWeekPeriod.value && isTimesheetMode.value);
  const currentWeekDates = computed(() => weekDates(currentWeekStart.value));
  // Неделя грузится вместе с месяцем её окончания: месячные consumers (StatBar, копирование, показатели)
  // остаются в своём контексте, а дни недели из предыдущего месяца тоже попадают в ответ.
  const scheduleRangeStart = computed(() => {
    if (!isWeekPeriod.value) return currentMonthStart.value;
    const weekStart = currentWeekStart.value.format(DATE_FORMAT);
    return weekStart < currentMonthStart.value ? weekStart : currentMonthStart.value;
  });
  const scheduleRangeEnd = computed(() => currentMonthEnd.value);
  // Ключ успешно загруженного контекста: без него неделя не рисует незагруженные дни как «Выходной»/«Нет отметок».
  const loadedScheduleKey = ref<string | null>(null);
  const scheduleContextKey = computed(() =>
    `${currentDepartment.value?.id ?? ""}|${scheduleRangeStart.value}|${scheduleRangeEnd.value}`,
  );
  const isScheduleContextLoaded = computed(() => loadedScheduleKey.value === scheduleContextKey.value);
  const timesheetContextKey = computed(() =>
    `${currentDepartment.value?.id ?? ""}|${currentScheduleDate.value.format("YYYY-MM")}`,
  );
  const isTimesheetContextLoaded = computed(() => loadedTimesheetKey.value === timesheetContextKey.value);
  // Часы смен месячного графика — из табеля открытого месяца (тот же POST, что у табеля, без второго запроса).
  // Пока табель не загружен для текущего подразделения/месяца, значений нет — не ноль и не прежний месяц.
  const monthShiftHours = computed(() => {
    const hours = new Map<string, TimesheetShiftHours>();
    if (isTimesheetContextLoaded.value && Array.isArray(timesheets.value)) {
      collectTimesheetShiftHours(timesheets.value, hours);
    }
    return hours;
  });
  const scheduleShiftHours = computed(() => (isWeekView.value ? weekShiftHours.value : monthShiftHours.value));
  const statsContextKey = computed(() => `${currentDepartment.value?.id ?? ""}|${currentScheduleDate.value.format("YYYY-MM")}`);
  const isStatsContextLoaded = computed(() => loadedStatsKey.value === statsContextKey.value);
  const weekTimesheetContextKey = computed(() =>
    `${currentDepartment.value?.id ?? ""}|${currentWeekStart.value.format(DATE_FORMAT)}`,
  );
  const isWeekTimesheetContextLoaded = computed(() => loadedWeekTimesheetKey.value === weekTimesheetContextKey.value);
  // Готовность и загрузка табеля текущего вида: месяц — табель открытого месяца, неделя — все её месяцы.
  const isTimesheetViewLoaded = computed(() => (isWeekPeriod.value
    ? isWeekTimesheetContextLoaded.value && !isWeekTimesheetRefreshPending.value
    : isTimesheetContextLoaded.value && !isTimesheetRefreshPending.value));
  const isTimesheetViewLoading = computed(() => (isWeekPeriod.value ? isWeekTimesheetLoading.value : isTimesheetLoading.value));
  // Табель по месяцу и ТУ: YYYY-MM → employment_id → элемент ответа. Связь со строкой — employment_id = schedule.id.
  // Ключ включает месяц: в неделе на стыке месяцев одинаковые DD разных месяцев не пересекаются.
  const timesheetIndex = computed(() => {
    const index = new Map<string, Map<string, any>>();
    if (!isTimesheetViewLoaded.value) return index;
    const months: Record<string, any[]> = isWeekPeriod.value
      ? weekTimesheets.value
      : { [currentScheduleDate.value.format("YYYY-MM")]: timesheets.value };
    for (const [month, items] of Object.entries(months)) {
      index.set(month, new Map(items.map(item => [item.employment_id, item])));
    }
    return index;
  });
  const getEmploymentTimesheet = (employmentId: string, month: string) => timesheetIndex.value.get(month)?.get(employmentId);
  const isNewShiftsExist = computed(() =>
    currentShifts.value.find((shift) => !shift.id),
  );

  // Функции для работы с графиками
  async function fetchStats() {
    const data = {
      department: currentDepartment.value?.id,
      month: currentScheduleDate.value.month() + 1,
      year: currentScheduleDate.value.year(),
    };
    const request = ++statsRequest;
    const key = statsContextKey.value;
    try {
      const res = await getStats(data);
      // Запоздалый ответ прежнего подразделения/периода не подменяет текущие показатели.
      if (request !== statsRequest) return;
      stats.value = res.data.body;
      loadedStatsKey.value = key;
    } catch (err: any) {
      if (request === statsRequest) loadedStatsKey.value = null;
      return err;
    }
  }

  async function fetchSchedule(options: { checkRules?: boolean; background?: boolean } = {}) {
    if (options.background) return refreshScheduleInBackground(options);
    const params = {
      department: currentDepartment?.value?.id,
      start_date: scheduleRangeStart.value,
      end_date: scheduleRangeEnd.value,
    };
    if (currentDepartment.value && authStore.permissions?.can_view_schedule_and_timesheet) {
      const request = ++scheduleRequest;
      try {
        loading.value = true;
        fetchProductionCalendar();
        if (options.checkRules !== false) checkRules();
        await fetchStats();
        const res = await getSchedule(params);
        // При быстрой навигации ответ прежней недели/подразделения не подменяет новый контекст.
        if (request !== scheduleRequest) return;
        schedules.value = res.data.body;
        loadedScheduleKey.value = `${params.department ?? ""}|${params.start_date}|${params.end_date}`;
        sortSchedules()
        refresh.value++;
        fetchWeekTimesheets();
      } catch (err: any) {
        return err;
      } finally {
        if (request === scheduleRequest) loading.value = false;
      }
    }
  }

  async function refreshScheduleInBackground(options: { checkRules?: boolean }) {
    if (!currentDepartment.value || !authStore.permissions?.can_view_schedule_and_timesheet) return;

    const context = currentRuleCheckContext();
    const request = ++backgroundScheduleRequest;
    const foregroundRequest = scheduleRequest;
    const params = {
      department: context.department,
      start_date: scheduleRangeStart.value,
      end_date: scheduleRangeEnd.value,
    };
    const results = await Promise.allSettled([
      getSchedule(params),
      options.checkRules === false ? Promise.resolve(undefined) : checkRules(context),
    ]);
    const isCurrentRequest = () => {
      const current = currentRuleCheckContext();
      return request === backgroundScheduleRequest && foregroundRequest === scheduleRequest &&
        current.department === context.department && current.month === context.month && current.year === context.year &&
        params.start_date === scheduleRangeStart.value && params.end_date === scheduleRangeEnd.value;
    };
    if (!isCurrentRequest()) return;

    // Показатели запрашиваем после обновления расписания и завершения checker.
    const [statsResult] = await Promise.allSettled([
      getStats({ department: context.department, month: Number(context.month), year: Number(context.year) }),
    ]);
    if (!isCurrentRequest()) return;

    const [scheduleResult, rulesResult] = results;
    const failed: string[] = [];
    if (scheduleResult.status === "fulfilled") {
      const updated: Schedule[] = scheduleResult.value.data.body;
      const byId = new Map(updated.map(schedule => [schedule.id, schedule]));
      const existingIds = new Set(schedules.value.map(schedule => schedule.id));
      schedules.value = [
        ...schedules.value.flatMap(schedule => {
          const replacement = byId.get(schedule.id);
          return replacement ? [replacement] : [];
        }),
        ...updated.filter(schedule => !existingIds.has(schedule.id)),
      ];
      sortSchedules();
      refreshShiftHours();
    } else failed.push("расписание");
    if (statsResult.status === "fulfilled") {
      stats.value = statsResult.value.data.body;
      loadedStatsKey.value = statsContextKey.value;
    } else failed.push("показатели сотрудников");
    if (options.checkRules !== false && (rulesResult.status === "rejected" || !rulesResult.value)) {
      failed.push("проверки правил графика");
    }
    if (failed.length) toast.error(`Не удалось обновить: ${failed.join(", ")}. Повторите обновление графика.`);
  }

  function sortSchedules() {
    schedulesSortedByName.value = schedules.value
    const main = schedules.value.filter(item => !item.is_vacancy && !item.is_outsource)
    const outsource = schedules.value.filter(item => item.is_vacancy && item.is_outsource)
    const vacancy = schedules.value.filter(item => item.is_vacancy && !item.is_outsource)
    main.sort((a, b) => a.position.name.localeCompare(b.position.name))
    outsource.sort((a, b) => a.position.name.localeCompare(b.position.name))
    vacancy.sort((a, b) => a.position.name.localeCompare(b.position.name))
    schedulesSortedByPosition.value = [...main, ...outsource, ...vacancy]
  }



  async function fetchProductionCalendar() {
    const params = {
      start_date: scheduleRangeStart.value,
      end_date: scheduleRangeEnd.value,
    };
    if (currentDepartment.value) {
      const request = ++prodCalendarRequest;
      try {
        const res = await getProductionCalendar(
          currentDepartment.value.id,
          params,
        );
        if (request !== prodCalendarRequest) return;
        prodCalendar.value = res.data.body;
      } catch (err: any) {
        return err;
      }
    }
  }

  function currentRuleCheckContext(): RuleCheckContext {
    return {
      department: currentDepartment.value?.id,
      month: currentScheduleDate.value.format("MM"),
      year: currentScheduleDate.value.format("YYYY"),
    };
  }

  async function checkRules(context = currentRuleCheckContext()) {
    if (!context.department) return;

    const isCurrentContext = () => {
      const current = currentRuleCheckContext();
      return current.department === context.department &&
        current.month === context.month && current.year === context.year;
    };
    const request = isCurrentContext() ? ++rulesRequest : undefined;
    const shouldApply = () => request !== undefined && request === rulesRequest && isCurrentContext();
    const result = await departmentStore.fetchDepartmentRules(
      context.department,
      { month: context.month, year: context.year },
      shouldApply,
    );
    if (result && shouldApply()) rules.value = result;
    return result;
  }

  async function fetchTimeSheet() {
    // В неделе (график и табель) месячные табели её дат грузит fetchWeekTimesheets —
    // не дублируем POST за тот же месяц. Табель загружается при входе в него (TimeSheetTable).
    // Счётчик сдвигается и здесь: запоздалый ответ прежнего месяца не применяется после перехода в неделю.
    const request = ++timesheetRequest;
    if (isWeekPeriod.value) {
      isTimesheetLoading.value = false;
      isTimesheetRefreshPending.value = false;
      return;
    }
    const key = timesheetContextKey.value;
    const data = {
      year: currentScheduleDate.value.format("YYYY"),
      month: currentScheduleDate.value.format("M"),
      department_id: currentDepartment.value?.id,
      scope: "with_other_deps",
    };
    if (currentDepartment.value && authStore.permissions?.can_view_schedule_and_timesheet) {
      isTimesheetLoading.value = true;
      try {
        const res = await getTimeSheet(data);
        if (request !== timesheetRequest) return;
        // Ошибочный/неполный ответ актуального запроса сбрасывает готовность контекста:
        // прежние часы не показываются как успешный расчёт (график — без часов, табель — сообщение об ошибке).
        if (Array.isArray(res.data?.body)) {
          timesheets.value = res.data.body;
          loadedTimesheetKey.value = key;
        } else {
          loadedTimesheetKey.value = null;
        }
      } catch (err: any) {
        // Запоздавшая ошибка прежнего запроса не сбрасывает более новый успешный результат.
        if (request === timesheetRequest) loadedTimesheetKey.value = null;
        return err;
      } finally {
        if (request === timesheetRequest) {
          isTimesheetLoading.value = false;
          isTimesheetRefreshPending.value = false;
        }
      }
    } else {
      isTimesheetRefreshPending.value = false;
    }
  }

  // Месячные табели всех месяцев, к которым относятся даты недели: отработанные часы недельного графика
  // и данные недельного табеля (RD-139). Один существующий POST на месяц, DTO и body запроса прежние.
  async function fetchWeekTimesheets() {
    const request = ++weekTimesheetRequest;
    if (!isWeekPeriod.value || !currentDepartment.value || !authStore.permissions?.can_view_schedule_and_timesheet) {
      weekShiftHours.value = new Map();
      isWeekTimesheetLoading.value = false;
      isWeekTimesheetRefreshPending.value = false;
      return;
    }
    const key = weekTimesheetContextKey.value;
    const departmentId = currentDepartment.value.id;
    const months = [...new Set(currentWeekDates.value.map(date => date.slice(0, 7)))];
    isWeekTimesheetLoading.value = true;
    const results = await Promise.allSettled(months.map(month => getTimeSheet({
      year: month.slice(0, 4),
      month: String(Number(month.slice(5, 7))),
      department_id: departmentId,
      scope: "with_other_deps",
    })));
    if (request !== weekTimesheetRequest) return;
    isWeekTimesheetLoading.value = false;
    isWeekTimesheetRefreshPending.value = false;
    const hours = new Map<string, TimesheetShiftHours>();
    const byMonth: Record<string, any[]> = {};
    let failed = false;
    results.forEach((result, index) => {
      if (result.status === "fulfilled" && Array.isArray(result.value?.data?.body)) {
        collectTimesheetShiftHours(result.value.data.body, hours);
        byMonth[months[index]] = result.value.data.body;
      } else failed = true;
    });
    weekShiftHours.value = hours;
    weekTimesheets.value = byMonth;
    // Неполная неделя не считается готовой: табель покажет ошибку, а не незагруженный месяц как выходные.
    loadedWeekTimesheetKey.value = failed ? null : key;
    // В табеле ошибку показывает сама таблица; в графике часы — дополнительные сведения, сообщаем toast.
    if (failed && !isTimesheetMode.value) toast.error("Не удалось загрузить отработанные часы из табеля. Обновите график.");
  }

  // Актуализация табельных данных после фонового обновления/сохранения/удаления смен.
  // Неделя — месячные табели её дат; месяц (график и табель) — табель открытого месяца. Первичную загрузку
  // месяца выполняет fetchTimeSheet вместе с fetchSchedule (reloadPeriod, выбор подразделения).
  // До ответа загруженный табель текущего вида считается устаревшим (TimeSheetTable показывает загрузку).
  function refreshShiftHours() {
    if (isWeekPeriod.value) isWeekTimesheetRefreshPending.value = true;
    else isTimesheetRefreshPending.value = true;
    fetchWeekTimesheets();
    if (!isWeekPeriod.value) fetchTimeSheet();
  }

  // Загрузка табеля текущего вида при входе в табель.
  function fetchTimesheetView() {
    if (isWeekPeriod.value) return fetchWeekTimesheets();
    return fetchTimeSheet();
  }

  async function fetchEmploymentSchedule(id: string) {
    const params = {
      start_date: currentMonthStart.value,
      end_date: currentMonthEnd.value,
    };
    try {
      const res = await getEmploymentSchedule(id, params);
      selectedEmploymentSchedule.value = res.data.body;
      return res.data.body;
    } catch (err: any) {
      return err;
    }
  }

  async function scheduleCleanup(data: any) {
    try {
      loading.value = true;
      const res = await cleanSchedule(data);
      toast.success(res.data.msg);
      setTimeout(fetchSchedule, 1000);
    } catch (err: any) {
      loading.value = false;
      return err;
    }
  }

  async function scheduleGenerate() {
    const data = {
      department: currentDepartment.value?.id,
      year: currentScheduleDate.value.format("YYYY"),
      month: currentScheduleDate.value.format("MM"),
    };
    try {
      loading.value = true;
      const res = await generateSchedule(data);
      toast.success(res.data.msg);
      await fetchSchedule();
    } catch (err: any) {
      loading.value = false;
      return err;
    }
  }

  async function fetchPositions() {
    try {
      const res = await getPositions();
      return (positions.value = res.data.body);
    } catch (err: any) {
      return err;
    }
  }

  function getPosition(id: string) {
    return positions.value.find((item) => item.id === id);
  }

  async function fetchDayTypes() {
    try {
      const res = await getDayTypes();
      return (dayTypes.value = res.data.body);
    } catch (err: any) {
      return err;
    }
  }

  function getDayType(id: string) {
    return dayTypes.value.find((item) => item.id === id);
  }

  async function fetchShiftSchedules() {
    try {
      const res = await getShiftSchedule();
      return (shiftSchedules.value = res.data.body);
    } catch (err: any) {
      return err;
    }
  }

  async function fetchShiftHistory(id: string) {
    try {
      shiftHistory.value = [];
      const res = await getShiftHistory(id);
      return (shiftHistory.value = res.data.body);
    } catch (err: any) {
      return err;
    }
  }

  async function shiftValidate(shift: any) {
    try {
      const res = await validateShift(shift);
      if (res.data.status === "ok") {
        isShiftValid.value = res.data.body.is_valid;
        const errors = res.data.body.errors;
        if (!res.data.body.is_valid && errors.length) {
          errors.forEach((error: string) => {
            toast.error(error);
          });
        }
      }
    } catch (err: any) {
      return err;
    }
  }

  async function handleSaveButtonAvailability() {
    if(authStore.settings?.USE_42_HOURS_REST_CHECK || authStore.settings?.USE_HOURS_LIMIT_CHECK) {
        await shiftValidate(currentShift.value);
    } else {
        isShiftValid.value = true
    }
    
    isSaveShiftEnabled.value =
      (isNewShiftsExist.value ||
        !!changedShifts.value.length) &&
      isShiftValid.value
  }

  async function shiftDelete(shift: any) {
    try {
      const res = await removeShift(shift.id);
      if (res.data.status === "ok") {
        toast.success(res.data.msg);
        updateScheduleDelete(shift);
        refreshShiftHours();
        await checkRules();
        return res.data.status;
      }
    } catch (err: any) {
      return err;
    }
  }

  // Результат: сохранённые смены (успех), [] — изменений нет и запрос не отправлялся,
  // null — запись не выполнена (status:error или отклонённый запрос; сообщение показывает общий HTTP-клиент).
  async function shiftPackCreateUpdate(data: object, refreshStats = true): Promise<Shift[] | null> {
    let saved: Shift[] | null = null;
    try {
      const payload = buildShiftPackPayload(data);

      if (!payload.length) return [];

      const checks = savedShiftRuleChecks(payload, data);
      const res = await createUpdateShiftPack(payload);
      if (res.data.status !== "ok") return null;

      saved = res.data.body ?? [];
      toast.success(res.data.msg);
      updateSchedulePack(res.data.body);
      updateSavedShiftBaselines(payload);
      bindCreatedShifts(payload, res.data.body);
      // Часы табеля пересчитываются после сохранения; до этого прежнее значение не совпадёт со сменой и не покажется.
      refreshShiftHours();
      const [, warnings] = await Promise.all([
        refreshStats ? fetchStats() : Promise.resolve(),
        collectSavedShiftWarnings(checks),
      ]);
      if (warnings.length) toast.error(warnings.join("<br>"));
      return saved;
    } catch (err: unknown) {
      console.error(err);
      // Сбой обновления данных после принятой записи не превращает сохранение в отказ.
      return saved;
    }
  }

  function savedShiftRuleChecks(payload: Partial<Shift>[], data: object) {
    const sourceShifts: Partial<Shift>[] = Array.isArray(data) ? data : [data];
    const fallback = currentRuleCheckContext();
    const checks = new Map<string, { context: RuleCheckContext; employments: Set<string> }>();

    for (const saved of payload) {
      // Diff-payload существующей смены содержит только id и изменённые поля.
      const source = saved.id ? sourceShifts.find(shift => shift.id === saved.id) : saved;
      const shift = { ...source, ...saved };
      const date = shift.date ? dayjs(shift.date) : undefined;
      const context = {
        department: shift.department ?? fallback.department,
        month: date ? date.format("MM") : fallback.month,
        year: date ? date.format("YYYY") : fallback.year,
      };
      const key = `${context.department}/${context.year}/${context.month}`;
      let check = checks.get(key);
      if (!check) {
        check = { context, employments: new Set<string>() };
        checks.set(key, check);
      }
      if (shift.employment) check.employments.add(shift.employment);
    }
    return [...checks.values()];
  }

  async function checkCopiedShiftRules(operation: ShiftCopyRequest) {
    const months = new Map(operation.target_dates.map(date => [date.slice(0, 7), dayjs(date)]));
    const employments = new Set(operation.employment_ids);
    return collectSavedShiftWarnings([...months.values()].map(date => ({
      context: {
        department: operation.department_id,
        month: date.format("MM"),
        year: date.format("YYYY"),
      },
      employments,
    })), true);
  }

  async function collectSavedShiftWarnings(checks: ReturnType<typeof savedShiftRuleChecks>, reportFailedChecks = false): Promise<string[]> {
    const results = await Promise.all(checks.map(async ({ context, employments }) => ({
      result: await checkRules(context),
      employments,
    })));
    if (results.some(({ result }) => !result)) {
      if (reportFailedChecks) toast.error("Не удалось обновить проверки правил графика. Повторите обновление графика.");
      return [];
    }

    const warnings: string[] = [];
    if (results.some(({ result, employments }) =>
      result?.rule_42_hours?.employments?.some(item => employments.has(item.employment_id)))) {
      warnings.push("Нарушение правила 42 часов");
    }
    if (results.some(({ result, employments }) =>
      result?.hours_limit_rule?.employments?.some(item =>
        !item.is_valid && employments.has(item.employment_id)))) {
      warnings.push("Превышена норма плановых часов");
    }
    return warnings;
  }

  const editableShiftFields = [
    "plan_work_start_at",
    "plan_work_end_at",
    "fact_work_start_at",
    "fact_work_end_at",
    "day_type",
    "position",
    "is_overtime",
    "duration",
    "day_state_document",
  ];
  const dateTimeShiftFields = [
    "plan_work_start_at",
    "plan_work_end_at",
    "fact_work_start_at",
    "fact_work_end_at",
  ];

  function buildShiftPackPayload(data: any) {
    const shifts = Array.isArray(data) ? data : [data];

    return shifts.reduce((payload: any[], shift: any) => {
      const shiftPayload = buildShiftPayload(shift);

      if (shiftPayload) payload.push(shiftPayload);

      return payload;
    }, []);
  }

  function buildShiftPayload(shift: any) {
    if (!shift?.id) return shift;

    const originalShift = originalShifts.value.find((item) => item.id === shift.id);

    if (!originalShift) return shift;

    const changedFields = editableShiftFields.reduce((payload: any, field) => {
      if (isShiftFieldChanged(shift, originalShift, field)) payload[field] = shift[field];

      return payload;
    }, { id: shift.id });

    return Object.keys(changedFields).length > 1 ? changedFields : null;
  }

  function isShiftFieldChanged(shift: any, originalShift: any, field: string) {
    if (dateTimeShiftFields.includes(field)) {
      return !isSameDateTime(shift[field], originalShift[field]);
    }

    return shift[field] !== originalShift[field];
  }

  function isSameDateTime(value: any, originalValue: any) {
    if (!value && !originalValue) return true;
    if (!value || !originalValue) return false;

    return dayjs.utc(value).format("HH:mm") === dayjs.utc(originalValue).format("HH:mm");
  }

  function updateSavedShiftBaselines(payload: any[]) {
    payload.forEach((shiftPayload) => {
      if (!shiftPayload?.id) return;

      mergeShiftById(originalShifts.value, shiftPayload);
      mergeShiftById(currentShifts.value, shiftPayload);

      if (currentShift.value?.id === shiftPayload.id) {
        currentShift.value = { ...currentShift.value, ...shiftPayload };
      }

      removeChangedShiftId(shiftPayload.id);
    });
  }

  // Созданная смена заменяет свой черновик в открытом редакторе: панель может остаться открытой
  // (сохранение из окна «Сохранить изменения?»), и без id смена считалась бы несохранённой.
  function bindCreatedShifts(payload: any[], saved: Shift[]) {
    const updatedIds = new Set(payload.map((shift) => shift.id));
    const created = saved.filter((shift) => !updatedIds.has(shift.id));

    payload.filter((shift) => !shift.id).forEach((draft, index) => {
      const draftIndex = currentShifts.value.indexOf(draft);

      if (draftIndex === -1 || !created[index]) return;

      const wasSelected = currentShift.value === currentShifts.value[draftIndex];

      currentShifts.value[draftIndex] = { ...created[index] };
      originalShifts.value.push({ ...created[index] });

      if (wasSelected) currentShift.value = currentShifts.value[draftIndex];
    });
  }

  function mergeShiftById(shifts: any[], shiftPayload: any) {
    const shiftIndex = shifts.findIndex((shift) => shift.id === shiftPayload.id);

    if (shiftIndex === -1) return;

    shifts[shiftIndex] = { ...shifts[shiftIndex], ...shiftPayload };
  }

  function updateScheduleDelete(shift: any) {
    schedules.value.map((item) => {
      if (item.id === shift.employment) {
        item.shifts = item.shifts.filter((sh) => sh.id !== shift.id);
        refresh.value++;
      }
    });
  }

  function updateSchedulePack(shifts: any[]) {
    schedules.value.map((item) => {
      if (item.id === shifts[0].employment) {
        item.shifts = item.shifts.filter(shift => shift.id !== shifts[0].id); // remove old shifts from temp schedule
        item.shifts.push(...shifts);
        refresh.value++;
      }
    });
  }

  function setCurrentShift(shift: Shift | null) {
    currentShift.value = shift;
  }

  function setCurrentShifts(shifts: Shift[]) {
    currentShifts.value = shifts;
  }

  function updateCurrentShifts(shift: Shift) {
    const shiftId = shift.id;
    currentShifts.value = currentShifts.value.filter(
      (shift) => shift.id !== shiftId,
    );
    currentShifts.value.push(shift);
  }

  function setOriginalShifts(shifts: Shift[]) {
    originalShifts.value = shifts;
  }
  // add shift id in changedShifts if changes detected
  function addChangedShiftId(id: string) {
    if (!changedShifts.value.length || !changedShifts.value.includes(id))
      changedShifts.value.push(id);
  }

  function removeChangedShiftId(id: string) {
    changedShifts.value = changedShifts.value.filter((item) => item !== id);
  }

  function setCurrentDate(date: any) {
    currentScheduleDate.value = date;
    // Ошибка storage (private mode, квота) не должна прерывать смену периода и загрузку.
    try {
      localStorage.setItem("date", JSON.stringify(date));
    } catch {
      // Дата не сохранится до перезагрузки, текущий период продолжит работать.
    }
  }

  function readStoredDate() {
    try {
      const dtjson = localStorage.getItem("date");
      const date = dtjson ? dayjs(JSON.parse(dtjson)) : null;
      return date?.isValid() ? date : null;
    } catch {
      return null;
    }
  }

  function getCurrentDate() {
    currentScheduleDate.value = readStoredDate() ?? dayjs();
    scheduleView.value = readStoredScheduleView();
    // В недельном виде сохранённая дата — воскресенье открытой недели.
    if (scheduleView.value === "week") {
      currentWeekStart.value = weekStartOf(currentScheduleDate.value);
      currentScheduleDate.value = currentWeekStart.value.add(6, "day");
    }
  }

  function reloadPeriod() {
    fetchSchedule();
    fetchTimeSheet();
  }

  // Неделя Пн–Вс; месячный контекст (табель, показатели, отчёты, автосоставление) — месяц воскресенья.
  function applyWeek(weekStart: Dayjs) {
    currentWeekStart.value = weekStartOf(weekStart);
    setCurrentDate(currentWeekStart.value.add(6, "day"));
  }

  function nextScheduleMonth() {
    setCurrentDate(currentScheduleDate.value.add(1, "month"));
    reloadPeriod();
  }

  function prevScheduleMonth() {
    setCurrentDate(currentScheduleDate.value.subtract(1, "month"));
    reloadPeriod();
  }

  function goToMonth(year: number, month: number) {
    setCurrentDate(currentScheduleDate.value.year(year).month(month).startOf("month"));
    reloadPeriod();
  }

  function nextSchedulePeriod() {
    if (!isWeekPeriod.value) return nextScheduleMonth();
    applyWeek(currentWeekStart.value.add(1, "week"));
    reloadPeriod();
  }

  function prevSchedulePeriod() {
    if (!isWeekPeriod.value) return prevScheduleMonth();
    applyWeek(currentWeekStart.value.subtract(1, "week"));
    reloadPeriod();
  }

  function goToWeek(date: Dayjs) {
    applyWeek(date);
    reloadPeriod();
  }

  // Переход к месяцу после месячной операции (копирование графика): в неделе — по правилу Месяц → Неделя.
  function setMonthContext(date: Dayjs) {
    if (isWeekPeriod.value) applyWeek(weekStartForMonth(date));
    else currentScheduleDate.value = date;
  }

  function setScheduleView(view: ScheduleView) {
    if (scheduleView.value === view) return;
    scheduleView.value = view;
    writeStoredScheduleView(view);
    // Неделя → Месяц: currentScheduleDate уже в месяце воскресенья.
    if (view === "week") applyWeek(weekStartForMonth(currentScheduleDate.value));
    schedules.value = [];
    reloadPeriod();
  }

  // График и табель используют один вид и период (RD-139): недельный график открывает недельный табель
  // той же недели, месяц — тот же месяц. Диапазон графика от режима не зависит, перезагрузка не нужна;
  // табель загружается при входе в него (TimeSheetTable).
  function setTimesheetMode(value: boolean) {
    isTimesheetMode.value = value;
  }

  function setCurrentDepartment(department: Department) {
    currentDepartment.value = department;
    localStorage.setItem("department", JSON.stringify(currentDepartment.value));
  }

  function getCurrentDepartment() {
    const depjson = localStorage.getItem("department");
    // RD-166: контекстом Расписания становится только подтверждённый доступный магазин (myDepartments only_leafs).
    // Сохранённая голова прежнего дерева, пустой или не загрузившийся список — без контекста, запросы не уходят.
    // Без сохранённого выбора первый магазин в порядке дерева выбирает DepartmentsTree, не порядок /me.
    const stored: Department | undefined = depjson ? JSON.parse(depjson) : undefined;
    const stores = departmentStore.myDepartments ?? [];
    currentDepartment.value = stored && stores.some((item) => item.id === stored.id) ? stored : undefined;
  }

  // Нет доступного магазина: запросы Расписания не уходят. Сохранённое значение в storage не трогаем.
  function clearCurrentDepartment() {
    currentDepartment.value = undefined;
  }

  // RD-166: голова (группа) выбирается только в Аналитике и хранится отдельно от currentDepartment,
  // чтобы её ID не попадал в запросы Расписания. currentDepartment остаётся последним выбранным магазином.
  function setAnalyticsGroup(group: AnalyticsGroup | null) {
    analyticsGroup.value = group;
  }

  function cutStr(str: string, len: number) {
    return str?.length > len ? str.slice(0, len) + "..." : str;
  }

  function addCurrentShift(shift: Shift) {
    currentShifts.value.push(shift);
  }

  function isExtraShift(shift: any) {
    return (
      shift.is_overtime ||
      shift.is_not_main_position ||
      shift.is_not_main_department ||
      shift.is_vacancy
    );
  }

  async function fetchReport(getReport: Function, reportName: string) {
    const data = {
      department_id: currentDepartment.value?.id,
      year: dayjs(currentScheduleDate.value).format("YYYY"),
      month: dayjs(currentScheduleDate.value).format("MM"),
    };
    try {
      const res = await getReport(data);
      const blob = new Blob([res.data]); // Get the response as a Blob
      const url = window.URL.createObjectURL(blob); // Create a URL for the Blob

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${reportName} за ${data.month}-${data.year}.xlsx`,
      ); // Set the desired filename and extension
      document.body.appendChild(link);
      link.click(); // Programmatically click the link to trigger download
      document.body.removeChild(link); // Clean up the link element
      window.URL.revokeObjectURL(url); // Release the object URL
    } catch (err: any) {
      return err;
    }
  }

  async function fetchTimesheetReport() {
    await fetchReport(getTimesheetReport, "Табель работы сотрудников");
  }

  async function fetchScheduleReport() {
    await fetchReport(getScheduleReport, "График работы сотрудников");
  }

  async function fetchConsolidatedReport() {
    await fetchReport(getConsolidatedReport, "Консолидированный отчет");
  }

  async function fetchFraudReport() {
    await fetchReport(getFraudReport, "Отчет о фроде");
  }

  async function fetchDisciplineReport() {
    await fetchReport(getDisciplineReport, "Отчет о дисциплине");
  }

  return {
    schedules,
    schedulesSortedByName,
    schedulesSortedByPosition,
    timesheets,
    weekShiftHours,
    scheduleShiftHours,
    isTimesheetLoading,
    isTimesheetContextLoaded,
    isTimesheetViewLoaded,
    isTimesheetViewLoading,
    isStatsContextLoaded,
    getEmploymentTimesheet,
    positions,
    dayTypes,
    currentShift,
    currentScheduleDate,
    currentDepartment,
    analyticsGroup,
    refresh,
    users,
    shiftSchedules,
    selectedEmploymentSchedule,
    currentShifts,
    originalShifts,
    shiftHistory,
    isSaveShiftEnabled,
    isCurrentShiftChanged,
    changedShifts,
    prodCalendar,
    shiftFieldsClicked,
    stats,
    userStat,
    userStat2,
    isLightTheme,
    isCellHighlighting,
    scheduleView,
    currentWeekStart,
    currentWeekDates,
    isTimesheetMode,
    isWeekPeriod,
    isWeekView,
    isTimesheetWeekView,
    isScheduleContextLoaded,
    isDayStatsVisible,
    isEmployeeStatsVisible,
    loading,
    rules,
    fetchSchedule,
    fetchTimeSheet,
    fetchTimesheetView,
    fetchPositions,
    fetchDayTypes,
    setCurrentShift,
    shiftPackCreateUpdate,
    checkCopiedShiftRules,
    nextScheduleMonth,
    prevScheduleMonth,
    goToMonth,
    nextSchedulePeriod,
    prevSchedulePeriod,
    setScheduleView,
    setTimesheetMode,
    goToWeek,
    setMonthContext,
    fetchShiftSchedules,
    cutStr,
    fetchEmploymentSchedule,
    shiftDelete,
    addCurrentShift,
    setCurrentShifts,
    setOriginalShifts,
    isExtraShift,
    fetchTimesheetReport,
    fetchConsolidatedReport,
    fetchScheduleReport,
    fetchFraudReport,
    fetchDisciplineReport,
    fetchShiftHistory,
    getDayType,
    getPosition,
    addChangedShiftId,
    removeChangedShiftId,
    fetchProductionCalendar,
    scheduleCleanup,
    shiftValidate,
    handleSaveButtonAvailability,
    scheduleGenerate,
    updateCurrentShifts,
    fetchStats,
    getCurrentDate,
    setCurrentDepartment,
    getCurrentDepartment,
    clearCurrentDepartment,
    setAnalyticsGroup,
    sortSchedules
  };
});
